import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Play,
  Pause,
  Square,
  Plus,
  Phone,
  Users,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  BarChart3,
  Settings,
  ChevronRight,
  Loader2,
  Zap,
  Calendar,
  PhoneOutgoing,
  Flame,
  TrendingUp,
  MoreVertical,
  Trash2,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_API_URL}/api`;

const STATUS_STYLES = {
  draft: { color: "bg-gray-500", label: "Draft", icon: Settings },
  active: { color: "bg-emerald-500", label: "Active", icon: Play },
  paused: { color: "bg-amber-500", label: "Paused", icon: Pause },
  completed: { color: "bg-blue-500", label: "Completed", icon: CheckCircle }
};

export default function CampaignQueues() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Create form
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    agent_id: "",
    contact_list_name: "",
    settings: {
      power_dialer_enabled: false,
      auto_advance_delay: 5,
      max_attempts_per_contact: 3,
      call_window_start: "09:00",
      call_window_end: "17:00"
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, agentsRes, listsRes] = await Promise.all([
        axios.get(`${API}/campaigns/queues`, { headers: getAuthHeader() }),
        axios.get(`${API}/agents`, { headers: getAuthHeader() }),
        axios.get(`${API}/phonebook/lists`, { headers: getAuthHeader() })
      ]);
      
      setCampaigns(campaignsRes.data.campaigns || []);
      setAgents(agentsRes.data || []);
      setContactLists(listsRes.data.lists || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name || !newCampaign.agent_id || !newCampaign.contact_list_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/campaigns/queue`, newCampaign, { headers: getAuthHeader() });
      toast.success("Campaign created successfully");
      setCreateDialogOpen(false);
      setNewCampaign({
        name: "",
        agent_id: "",
        contact_list_name: "",
        settings: {
          power_dialer_enabled: false,
          auto_advance_delay: 5,
          max_attempts_per_contact: 3,
          call_window_start: "09:00",
          call_window_end: "17:00"
        }
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create campaign");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateStatus = async (campaignId, newStatus) => {
    try {
      await axios.put(
        `${API}/campaigns/queue/${campaignId}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success(`Campaign ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update campaign status");
    }
  };

  const getProgress = (campaign) => {
    const stats = campaign.stats || {};
    const total = stats.total_contacts || 1;
    const made = stats.calls_made || 0;
    return Math.round((made / total) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="campaign-queues-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">
            Campaign Queues
          </h1>
          <p className="text-muted-foreground">
            Create and manage outbound calling campaigns
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-campaign-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{campaigns.length}</p>
                <p className="text-sm text-muted-foreground">Total Campaigns</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Play className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {campaigns.filter(c => c.status === "active").length}
                </p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <PhoneOutgoing className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {campaigns.reduce((sum, c) => sum + (c.stats?.calls_made || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Calls Made</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Flame className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {campaigns.reduce((sum, c) => sum + (c.stats?.leads_generated || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Leads Generated</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Target className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">No campaigns yet</h3>
            <p className="text-muted-foreground mb-4 text-center">
              Create your first outbound calling campaign to start reaching contacts
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => {
            const status = STATUS_STYLES[campaign.status] || STATUS_STYLES.draft;
            const StatusIcon = status.icon;
            const progress = getProgress(campaign);
            const stats = campaign.stats || {};
            const agent = agents.find(a => a.id === campaign.agent_id);
            
            return (
              <Card key={campaign.id} className="hover:border-primary/30 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    {/* Campaign Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold">{campaign.name}</h3>
                        <Badge className={status.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                        {campaign.settings?.power_dialer_enabled && (
                          <Badge variant="outline" className="text-amber-400 border-amber-400">
                            <Zap className="w-3 h-3 mr-1" />
                            Power Dialer
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {agent?.name || "Unknown Agent"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {campaign.contact_list_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {campaign.settings?.call_window_start} - {campaign.settings?.call_window_end}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span>Progress</span>
                          <span>{stats.calls_made || 0} / {stats.total_contacts || 0} contacts</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-emerald-400" />
                          <span>{stats.calls_connected || 0} connected</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <XCircle className="w-4 h-4 text-amber-400" />
                          <span>{stats.calls_failed || 0} failed</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Flame className="w-4 h-4 text-red-400" />
                          <span>{stats.leads_generated || 0} hot leads</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {campaign.status === "draft" && (
                        <Button 
                          onClick={() => handleUpdateStatus(campaign.id, "active")}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Start
                        </Button>
                      )}
                      {campaign.status === "active" && (
                        <Button 
                          variant="outline"
                          onClick={() => handleUpdateStatus(campaign.id, "paused")}
                        >
                          <Pause className="w-4 h-4 mr-2" />
                          Pause
                        </Button>
                      )}
                      {campaign.status === "paused" && (
                        <Button 
                          onClick={() => handleUpdateStatus(campaign.id, "active")}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Resume
                        </Button>
                      )}
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <BarChart3 className="w-4 h-4 mr-2" />
                            View Stats
                          </DropdownMenuItem>
                          {campaign.status !== "completed" && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleUpdateStatus(campaign.id, "completed")}
                              >
                                <Square className="w-4 h-4 mr-2" />
                                Mark Complete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Campaign Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="create-campaign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Create Campaign Queue
            </DialogTitle>
            <DialogDescription>
              Set up an outbound calling campaign with your AI agent
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Campaign Name */}
            <div className="space-y-2">
              <Label>Campaign Name *</Label>
              <Input
                placeholder="e.g., Q1 Sales Outreach"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                data-testid="campaign-name-input"
              />
            </div>

            {/* Agent Selection */}
            <div className="space-y-2">
              <Label>AI Agent *</Label>
              <Select
                value={newCampaign.agent_id}
                onValueChange={(v) => setNewCampaign({...newCampaign, agent_id: v})}
              >
                <SelectTrigger data-testid="agent-select">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contact List Selection */}
            <div className="space-y-2">
              <Label>Contact List *</Label>
              <Select
                value={newCampaign.contact_list_name}
                onValueChange={(v) => setNewCampaign({...newCampaign, contact_list_name: v})}
              >
                <SelectTrigger data-testid="list-select">
                  <SelectValue placeholder="Select a contact list" />
                </SelectTrigger>
                <SelectContent>
                  {contactLists.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      No contact lists. Import contacts first.
                    </div>
                  ) : (
                    contactLists.map((list) => (
                      <SelectItem key={list.name} value={list.name}>
                        {list.name} ({list.not_called} pending)
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {contactLists.length === 0 && (
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-xs"
                  onClick={() => navigate('/dashboard/phonebook')}
                >
                  Import contacts in Phonebook →
                </Button>
              )}
            </div>

            {/* Power Dialer Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400" />
                  <span className="font-medium">Power Dialer</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Auto-dial next contact after each call
                </p>
              </div>
              <Switch
                checked={newCampaign.settings.power_dialer_enabled}
                onCheckedChange={(v) => setNewCampaign({
                  ...newCampaign,
                  settings: {...newCampaign.settings, power_dialer_enabled: v}
                })}
                data-testid="power-dialer-toggle"
              />
            </div>

            {/* Call Window */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Call Window Start</Label>
                <Input
                  type="time"
                  value={newCampaign.settings.call_window_start}
                  onChange={(e) => setNewCampaign({
                    ...newCampaign,
                    settings: {...newCampaign.settings, call_window_start: e.target.value}
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Call Window End</Label>
                <Input
                  type="time"
                  value={newCampaign.settings.call_window_end}
                  onChange={(e) => setNewCampaign({
                    ...newCampaign,
                    settings: {...newCampaign.settings, call_window_end: e.target.value}
                  })}
                />
              </div>
            </div>

            {/* Max Attempts */}
            <div className="space-y-2">
              <Label>Max Call Attempts per Contact</Label>
              <Select
                value={String(newCampaign.settings.max_attempts_per_contact)}
                onValueChange={(v) => setNewCampaign({
                  ...newCampaign,
                  settings: {...newCampaign.settings, max_attempts_per_contact: parseInt(v)}
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 attempt</SelectItem>
                  <SelectItem value="2">2 attempts</SelectItem>
                  <SelectItem value="3">3 attempts</SelectItem>
                  <SelectItem value="5">5 attempts</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCampaign} disabled={creating}>
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
